import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Plus, Trash2, Share2, PieChart, Sparkles, Download, ThumbsUp, Search, MapPin, Users, Wallet, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api, { apiErr, resolveImg } from "@/lib/api";

const fmt = (n) => (Number(n) || 0).toLocaleString("fr-FR");

const MAJORS = [
  { code: "75056", name: "Paris", ll: [48.8566, 2.3522] },
  { code: "13055", name: "Marseille", ll: [43.2965, 5.3698] },
  { code: "69123", name: "Lyon", ll: [45.758, 4.835] },
  { code: "31555", name: "Toulouse", ll: [43.6045, 1.444] },
  { code: "06088", name: "Nice", ll: [43.7102, 7.262] },
  { code: "44109", name: "Nantes", ll: [47.2184, -1.5536] },
  { code: "34172", name: "Montpellier", ll: [43.6108, 3.8767] },
  { code: "67482", name: "Strasbourg", ll: [48.5734, 7.7521] },
  { code: "33063", name: "Bordeaux", ll: [44.8378, -0.5792] },
  { code: "59350", name: "Lille", ll: [50.6292, 3.0573] },
  { code: "35238", name: "Rennes", ll: [48.1173, -1.6778] },
];

const PRESETS = {
  demographie: {
    label: "Démographie française",
    blurb: "La population française répartie par tranche d'âge — une part de cocarde par génération.",
    items: [
      { label: "0–14 ans", percent: 17 }, { label: "15–29 ans", percent: 17 }, { label: "30–44 ans", percent: 19 },
      { label: "45–59 ans", percent: 20 }, { label: "60–74 ans", percent: 17 }, { label: "75 ans et +", percent: 10 },
    ],
  },
  budget: {
    label: "Budget de l'État",
    blurb: "Comment se découpe la dépense publique. Chaque part = un grand poste de l'État.",
    items: [
      { label: "Éducation", percent: 21 }, { label: "Solidarité", percent: 15 }, { label: "Dette", percent: 13 },
      { label: "Défense", percent: 11 }, { label: "Recherche", percent: 10 }, { label: "Écologie", percent: 9 },
      { label: "Sécurité", percent: 6 }, { label: "Autres", percent: 15 },
    ],
  },
  elections: {
    label: "Intentions de vote 🗳️",
    blurb: "Le sondage Cocarde — totalement fictif et gourmand ! Chaque part représente une liste… de pizzas.",
    items: [
      { label: "Liste Margherita", percent: 24 }, { label: "Liste Regina", percent: 21 }, { label: "Liste Diavola", percent: 18 },
      { label: "Liste 4 Fromages", percent: 15 }, { label: "Liste Calzone", percent: 12 }, { label: "Abstention", percent: 10 },
    ],
  },
};

const rad = (a) => ((a - 90) * Math.PI) / 180;
const pt = (cx, cy, r, a) => [cx + r * Math.cos(rad(a)), cy + r * Math.sin(rad(a))];

const TEMPLATES = {
  perso: { label: "Budget mensuel", title: "Mon budget mensuel",
    rows: [{ label: "Logement", percent: 30 }, { label: "Courses", percent: 20 }, { label: "Transport", percent: 20 }, { label: "Loisirs", percent: 15 }, { label: "Épargne", percent: 10 }, { label: "Pizza", percent: 5 }] },
  hf: { label: "Répartition H/F", title: "Répartition Femmes / Hommes",
    rows: [{ label: "Femmes", percent: 50 }, { label: "Hommes", percent: 50 }] },
  annuel: { label: "Budget annuel entreprise", title: "Budget annuel de l'entreprise",
    rows: [{ label: "Masse salariale", percent: 45 }, { label: "Achats & fournisseurs", percent: 20 }, { label: "Loyer & charges", percent: 12 }, { label: "Marketing & com", percent: 8 }, { label: "R&D / Investissement", percent: 10 }, { label: "Impôts & taxes", percent: 5 }] },
};

const loadImg = (src) => new Promise((res, rej) => { const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = src; });
const blobToDataURL = (blob) => new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(blob); });

// La vraie cocarde tricolore (rouge/blanc/bleu) découpée en parts — pas de couleur par catégorie.
// La part sélectionnée se détache. Chaque part porte un numéro (rappelé dans la légende).
function CocardeChart({ items, size = 280, id, selected = -1, onSelect, showNumbers = true }) {
  const total = items.reduce((s, i) => s + (Number(i.percent) || 0), 0) || 1;
  const cx = size / 2, cy = size / 2;
  const off = size > 160 ? 12 : 7;          // décalage de la part détachée
  const rRim = size / 2 - (off + 6);         // marge interne pour que la part ne soit pas coupée
  const rRed = rRim - 4;
  const rWhite = rRed * 0.7;
  const rBlue = rRed * 0.4;
  const wedge = (r, start, end) => {
    const [x1, y1] = pt(cx, cy, r, start);
    const [x2, y2] = pt(cx, cy, r, end);
    const large = end - start > 180 ? 1 : 0;
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
  };
  let acc = 0;
  const slices = items.map((it, idx) => {
    const val = (Number(it.percent) || 0) / total;
    const start = acc * 360; acc += val; const end = acc * 360;
    const mid = (start + end) / 2;
    const ox = Math.cos(rad(mid)) * off, oy = Math.sin(rad(mid)) * off;
    const [nx, ny] = pt(cx, cy, rRed * 0.85, mid);
    return { start, end, mid, ox, oy, nx, ny, idx, pct: Math.round(val * 100) };
  });
  return (
    <svg id={id} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="w-full h-full drop-shadow-2xl">
      <circle cx={cx} cy={cy} r={rRim} fill="#091E42" />
      {slices.map((s) => {
        const isSel = selected === s.idx;
        return (
          <motion.g key={s.idx} onClick={() => onSelect && onSelect(s.idx)}
            style={{ cursor: onSelect ? "pointer" : "default" }}
            animate={{ x: isSel ? s.ox : 0, y: isSel ? s.oy : 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}>
            <path d={wedge(rRed, s.start, s.end)} fill="#E11D48" stroke="#fff" strokeWidth="2" />
            <path d={wedge(rWhite, s.start, s.end)} fill="#ffffff" stroke="#ffffff" strokeWidth="0.5" />
            <path d={wedge(rBlue, s.start, s.end)} fill="#1D4ED8" stroke="#ffffff" strokeWidth="0.5" />
            {isSel && <path d={wedge(rRed, s.start, s.end)} fill="none" stroke="#F2C14E" strokeWidth="4" />}
            {showNumbers && s.pct >= 3 && (
              <g>
                <circle cx={s.nx} cy={s.ny} r="11" fill="#091E42" stroke="#fff" strokeWidth="2" />
                <text x={s.nx} y={s.ny} textAnchor="middle" dominantBaseline="central" fontSize="12" fontWeight="800" fill="#fff">{s.idx + 1}</text>
              </g>
            )}
          </motion.g>
        );
      })}
      <circle cx={cx} cy={cy} r={Math.max(4, size * 0.02)} fill="#fff" opacity="0.9" />
    </svg>
  );
}

const Legend = ({ items, selected = -1, onSelect, withValue = false, unit = "" }) => {
  const total = items.reduce((s, i) => s + (Number(i.percent) || 0), 0) || 1;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
      {items.map((it, i) => (
        <button key={i} type="button" onClick={() => onSelect && onSelect(selected === i ? -1 : i)} data-testid={`legend-item-${i}`}
          className={`flex items-center gap-2 text-sm text-left rounded-lg px-2 py-1 transition-all ${onSelect ? "hover:bg-slate-100" : ""} ${selected === i ? "bg-cc-gold/20 ring-1 ring-cc-gold" : ""}`}>
          <span className="w-5 h-5 rounded-full bg-cc-navy text-white text-[11px] font-black grid place-items-center shrink-0">{i + 1}</span>
          <span className="cc-navy font-semibold truncate">{it.label}</span>
          <span className="text-slate-400 ml-auto font-mono whitespace-nowrap">
            {withValue && it.value != null ? `${fmt(it.value)}${unit ? " " + unit : ""} · ` : ""}{Math.round(((Number(it.percent) || 0) / total) * 100)}%
          </span>
        </button>
      ))}
    </div>
  );
};

// ------------------- Recherche + carte + budget réel des villes -------------------
function CityExplorer() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [openList, setOpenList] = useState(false);
  const [city, setCity] = useState(null);
  const [sel, setSel] = useState(-1);
  const [bsel, setBsel] = useState(-1);
  const [budget, setBudget] = useState(null);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const boxRef = useRef(null);
  const tRef = useRef(null);
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const selectRef = useRef(null);

  const selectCommune = async (code) => {
    setOpenList(false);
    try {
      const { data } = await api.get(`/communes/${code}`);
      setCity(data); setSel(-1); setBsel(-1); setBudget(null); setBudgetLoading(true);
      setQ(`${data.nom} (${data.dept_code})`);
      if (mapRef.current && data.lat && data.lng) {
        const ll = [data.lat, data.lng];
        if (markerRef.current) markerRef.current.remove();
        markerRef.current = L.marker(ll, { icon: cocardeIcon() }).addTo(mapRef.current);
        mapRef.current.flyTo(ll, 11, { duration: 0.8 });
      }
      if (!data.items?.length) toast.info("Données d'âge indisponibles pour cette commune.");
      try {
        const b = await api.get(`/communes/${code}/budget`);
        setBudget(b.data);
      } catch { setBudget(null); } finally { setBudgetLoading(false); }
    } catch (e) { toast.error(apiErr(e)); setBudgetLoading(false); }
  };
  selectRef.current = selectCommune;

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpenList(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, { scrollWheelZoom: false }).setView([46.6, 2.4], 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "&copy; OpenStreetMap", maxZoom: 18 }).addTo(map);
    MAJORS.forEach((c) => {
      const m = L.marker(c.ll, { icon: cocardeIcon(28) }).addTo(map);
      m.bindTooltip(c.name, { direction: "top" });
      m.on("click", () => selectRef.current && selectRef.current(c.code));
    });
    map.on("click", async (e) => {
      const { lat, lng } = e.latlng;
      try {
        const res = await fetch(`https://geo.api.gouv.fr/communes?lat=${lat}&lon=${lng}&fields=nom,code&format=json`);
        const arr = await res.json();
        if (arr && arr[0]) selectRef.current && selectRef.current(arr[0].code);
        else toast.error("Aucune commune ici (mer ou hors France ?)");
      } catch { toast.error("Impossible de localiser cette zone"); }
    });
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 300);
  }, []);

  useEffect(() => {
    if (tRef.current) clearTimeout(tRef.current);
    if (q.trim().length < 1) { setResults([]); return; }
    tRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/communes/search`, { params: { q, limit: 20 } });
        setResults(data); setOpenList(true);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(tRef.current);
  }, [q]);

  const items = city?.items || [];

  return (
    <section className="bg-cc-navy text-white py-16 relative overflow-hidden grain">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <span className="eyebrow text-cc-gold flex items-center gap-2 justify-center"><MapPin size={15} /> 34 969 communes · INSEE + budgets OFGL</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-2">EXPLOREZ VOTRE VILLE SUR LA COCARDE</h2>
          <p className="text-white/70 mt-3">Cliquez sur la carte ou cherchez une commune — sa démographie et son budget réel se découpent sur la cocarde.</p>
        </div>

        <div ref={boxRef} className="relative max-w-xl mx-auto mb-6">
          <div className="flex items-center gap-2 bg-white rounded-2xl px-4 py-3 shadow-xl">
            <Search size={20} className="text-slate-400 shrink-0" />
            <input data-testid="city-search-input" value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setOpenList(true)}
              placeholder="Ex : Marseille, Lyon, 75011, Bordeaux…"
              className="flex-1 outline-none cc-navy font-semibold placeholder:text-slate-400 bg-transparent" />
          </div>
          {openList && results.length > 0 && (
            <div data-testid="city-search-results" className="absolute z-20 left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl overflow-hidden max-h-80 overflow-y-auto">
              {results.map((c) => (
                <button key={c.code} data-testid={`city-result-${c.code}`} onClick={() => selectCommune(c.code)}
                  className="w-full text-left px-4 py-2.5 flex items-center justify-between hover:bg-slate-100 border-b border-slate-50 last:border-0">
                  <span className="cc-navy font-bold truncate">{c.nom} <span className="text-slate-400 font-normal">· {c.dept} ({c.dept_code})</span></span>
                  <span className="text-slate-400 text-xs font-mono shrink-0 ml-3">{c.cp[0]} · {fmt(c.pop)} hab.</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div ref={mapEl} data-testid="pizza-map" className="w-full h-[360px] rounded-3xl overflow-hidden border-2 border-white/10 z-0 mb-4" />
        <p className="text-center text-white/50 text-xs mb-10">🖱️ Cliquez n'importe où sur la carte pour découper la ville correspondante.</p>

        {city ? (
          <div className="space-y-8">
            <div data-testid="city-detail" className="card-cocarde bg-white/95 p-6 sm:p-10 grid md:grid-cols-2 gap-8 items-center text-cc-navy">
              <motion.div key={city.code} initial={{ rotate: -12, scale: 0.85, opacity: 0 }} animate={{ rotate: 0, scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 90, damping: 12 }} className="max-w-[340px] w-full mx-auto">
                {items.length ? <CocardeChart items={items} selected={sel} onSelect={(i) => setSel(sel === i ? -1 : i)} />
                  : <div className="aspect-square grid place-items-center text-slate-400">Données d'âge indisponibles</div>}
              </motion.div>
              <div>
                <span className="eyebrow text-cc-red flex items-center gap-2"><Users size={15} /> Démographie · tranches d'âge</span>
                <h3 data-testid="city-name" className="font-display text-3xl mt-1">{city.nom}</h3>
                <p className="text-slate-500 text-sm mt-1">{city.dept} · {city.region} · CP {city.cp.join(", ")}</p>
                <div className="flex items-center gap-2 mt-3 mb-5">
                  <Users size={20} className="text-cc-red" />
                  <span data-testid="city-pop" className="font-display text-2xl text-cc-red">{fmt(city.pop)}</span>
                  <span className="text-slate-500 text-sm">habitants</span>
                </div>
                {items.length > 0 && <Legend items={items} selected={sel} onSelect={setSel} withValue />}
              </div>
            </div>

            {budgetLoading ? (
              <div data-testid="city-budget-loading" className="card-cocarde bg-white/95 p-8 text-center text-slate-400">Chargement du budget réel de la ville…</div>
            ) : budget && budget.items?.length > 0 ? (
              <div data-testid="city-budget" className="card-cocarde bg-white/95 p-6 sm:p-10 grid md:grid-cols-2 gap-8 items-center text-cc-navy">
                <div>
                  <span className="eyebrow text-cc-blue flex items-center gap-2"><Wallet size={15} /> Budget de la ville · ramené à 1 M€</span>
                  <h3 className="font-display text-3xl mt-1">Où va l'argent de {city.nom} ?</h3>
                  <p className="text-slate-500 text-sm mt-1 mb-2">Chaque part = un grand poste de dépense, ramené à <strong>1 000 000 €</strong> pour comparer toutes les villes.</p>
                  {budget.total_real && (
                    <p data-testid="city-budget-total" className="text-sm mb-3"><span className="font-display text-xl text-cc-red">{fmt(budget.total_real)} €</span> <span className="text-slate-500">de dépenses réelles{budget.year ? ` en ${budget.year}` : ""}</span></p>
                  )}
                  <Legend items={budget.items} selected={bsel} onSelect={setBsel} withValue unit="€" />
                  <p className="text-[11px] text-slate-400 mt-3">Source : {budget.source}{budget.year ? ` · exercice ${budget.year}` : ""}.</p>
                </div>
                <motion.div key={city.code + "-b"} initial={{ rotate: 12, scale: 0.85, opacity: 0 }} animate={{ rotate: 0, scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 90, damping: 12 }} className="max-w-[340px] w-full mx-auto order-first md:order-last">
                  <CocardeChart items={budget.items} selected={bsel} onSelect={(i) => setBsel(bsel === i ? -1 : i)} />
                </motion.div>
              </div>
            ) : null}
          </div>
        ) : (
          <p className="text-center text-white/50 text-sm">Cliquez sur la carte ou tapez une commune pour explorer les 34 969 villes de France.</p>
        )}
      </div>
    </section>
  );
}

function cocardeIcon(size = 34) {
  return L.divIcon({
    className: "",
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:#fff;border:3px solid #E11D48;box-shadow:0 3px 8px rgba(0,0,0,.35);overflow:hidden"><img src="/assets/logo-icon.png" style="width:100%;height:100%;object-fit:contain"/></div>`,
    iconSize: [size, size], iconAnchor: [size / 2, size / 2],
  });
}

export default function Pizzagraphie() {
  const [preset, setPreset] = useState("demographie");
  const [presetSel, setPresetSel] = useState(-1);
  const [rows, setRows] = useState([{ label: "Logement", percent: 30 }, { label: "Courses", percent: 20 }, { label: "Transport", percent: 20 }, { label: "Loisirs", percent: 15 }, { label: "Épargne", percent: 10 }, { label: "Pizza", percent: 5 }]);
  const [title, setTitle] = useState("Mon budget mensuel");
  const [author, setAuthor] = useState("");
  const [logo, setLogo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [builderSel, setBuilderSel] = useState(-1);
  const [community, setCommunity] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/budgets").then((r) => setCommunity(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const presetData = PRESETS[preset];
  const customTotal = useMemo(() => rows.reduce((s, r) => s + (Number(r.percent) || 0), 0), [rows]);

  const setRow = (i, k, v) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows([...rows, { label: "", percent: 10 }]);
  const delRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const applyTemplate = (k) => { const t = TEMPLATES[k]; setTitle(t.title); setRows(t.rows.map((r) => ({ ...r }))); setBuilderSel(-1); };

  const submit = async () => {
    const items = rows.filter((r) => r.label && Number(r.percent) > 0).map((r) => ({ label: r.label, percent: Number(r.percent) }));
    if (!items.length) { toast.error("Ajoutez au moins une part valide"); return; }
    setBusy(true);
    try {
      await api.post("/budgets", { title: title || "Mon budget", author_name: author || "Anonyme", theme: "Perso", items, logo_url: logo });
      toast.success("Cocarde envoyée ! Elle sera visible après modération par l'équipe.");
      setLogo(null);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/budgets/logo", fd); setLogo(data.url); toast.success("Logo ajouté au centre de la cocarde"); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const exportPng = async () => {
    const svg = document.getElementById("builder-svg");
    if (!svg) return;
    const legend = rows.filter((r) => r.label && Number(r.percent) > 0);
    const totalP = legend.reduce((s, r) => s + Number(r.percent), 0) || 1;
    const xml = new XMLSerializer().serializeToString(svg);
    let baseImg, logoImg = null;
    try { baseImg = await loadImg("data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(xml)))); }
    catch { toast.error("Export impossible"); return; }
    if (logo) {
      try { const blob = await fetch(resolveImg(logo)).then((r) => r.blob()); logoImg = await loadImg(await blobToDataURL(blob)); } catch { /* logo optionnel */ }
    }
    const W = 1080, top = 150, chart = 600, legendTop = top + chart + 55, rowH = 66;
    const H = legendTop + legend.length * rowH + 100;
    const c = document.createElement("canvas"); c.width = W; c.height = H;
    const ctx = c.getContext("2d");
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#091E42"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#E4A63C"; ctx.fillRect(0, 0, W, 14);
    ctx.fillStyle = "#E11D48"; ctx.fillRect(0, H - 14, W, 14);
    ctx.fillStyle = "#fff"; ctx.textAlign = "center"; ctx.font = "800 58px Poppins, Arial, sans-serif";
    ctx.fillText((title || "Mon budget").slice(0, 26), W / 2, 92);
    ctx.drawImage(baseImg, (W - chart) / 2, top, chart, chart);
    if (logoImg) {
      const lcx = W / 2, lcy = top + chart / 2, lr = chart * 0.13;
      ctx.save();
      ctx.beginPath(); ctx.arc(lcx, lcy, lr + 6, 0, Math.PI * 2); ctx.fillStyle = "#fff"; ctx.fill();
      ctx.beginPath(); ctx.arc(lcx, lcy, lr, 0, Math.PI * 2); ctx.clip();
      ctx.drawImage(logoImg, lcx - lr, lcy - lr, lr * 2, lr * 2);
      ctx.restore();
    }
    legend.forEach((r, i) => {
      const y = legendTop + i * rowH + rowH / 2;
      const pct = Math.round((Number(r.percent) / totalP) * 100);
      ctx.beginPath(); ctx.fillStyle = "#0b2a5b"; ctx.arc(150, y, 24, 0, Math.PI * 2); ctx.fill();
      ctx.lineWidth = 3; ctx.strokeStyle = "#ffffff"; ctx.stroke();
      ctx.fillStyle = "#fff"; ctx.textAlign = "center"; ctx.font = "800 28px Poppins, Arial, sans-serif";
      ctx.fillText(String(i + 1), 150, y + 1);
      ctx.textAlign = "left"; ctx.fillStyle = "#fff"; ctx.font = "700 34px Poppins, Arial, sans-serif";
      ctx.fillText(r.label.slice(0, 24), 195, y + 1);
      ctx.textAlign = "right"; ctx.fillStyle = "#F2C14E"; ctx.font = "800 34px Poppins, Arial, sans-serif";
      ctx.fillText(pct + "%", W - 80, y + 1);
    });
    ctx.textAlign = "center"; ctx.fillStyle = "#F2C14E"; ctx.font = "800 40px Poppins, Arial, sans-serif";
    ctx.fillText("#cocardepizza", W / 2, H - 48);
    setPreview(c.toDataURL("image/png"));
  };

  const voteBudget = async (id) => {
    try {
      const { data } = await api.post(`/budgets/${id}/vote`);
      setCommunity((c) => c.map((b) => (b.id === id ? { ...b, votes: data.votes } : b)).sort((a, b) => b.votes - a.votes));
      localStorage.setItem(`cc_bvote_${id}`, "1");
      toast.success("Merci pour votre vote 🍕");
    } catch (e) { toast.error(apiErr(e)); localStorage.setItem(`cc_bvote_${id}`, "1"); }
  };

  return (
    <div>
      <section className="bg-cc-navy text-white py-14 relative overflow-hidden grain">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <span className="eyebrow text-cc-gold">L'actu en parts de cocarde</span>
          <h1 className="font-display text-5xl sm:text-6xl mt-3">LA PIZZAGRAPHIE 🍕</h1>
          <p className="text-white/70 mt-4 max-w-2xl mx-auto">Démographie, budgets, sondages… On découpe la France sur la cocarde. Explorez votre ville, puis créez la vôtre et partagez-la !</p>
        </div>
      </section>

      <CityExplorer />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="text-center mb-6">
          <span className="eyebrow text-cc-red">Les grands chiffres de la France</span>
          <h2 className="font-display text-3xl sm:text-4xl cc-navy mt-1">DÉCOUPÉS SUR LA COCARDE</h2>
        </div>
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {Object.entries(PRESETS).map(([k, v]) => (
            <button key={k} onClick={() => { setPreset(k); setPresetSel(-1); }} data-testid={`preset-${k}`}
              className={`rounded-full px-4 py-2 text-sm font-bold transition-all ${preset === k ? "bg-cc-red text-white shadow-lg" : "bg-slate-100 cc-navy hover:bg-slate-200"}`}>
              {v.label}
            </button>
          ))}
        </div>
        <div className="card-cocarde p-6 sm:p-10 grid md:grid-cols-2 gap-8 items-center">
          <motion.div key={preset} initial={{ rotate: -12, scale: 0.85, opacity: 0 }} animate={{ rotate: 0, scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 90, damping: 12 }} className="max-w-[340px] w-full mx-auto" data-testid="preset-chart">
            <CocardeChart items={presetData.items} selected={presetSel} onSelect={(i) => setPresetSel(presetSel === i ? -1 : i)} />
          </motion.div>
          <div>
            <h2 className="font-display text-3xl cc-navy">{presetData.label}</h2>
            <p className="text-slate-500 mt-2 mb-5">{presetData.blurb}</p>
            <Legend items={presetData.items} selected={presetSel} onSelect={setPresetSel} />
          </div>
        </div>
      </section>

      <section className="bg-slate-100 py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <span className="eyebrow text-cc-red flex items-center gap-2 justify-center"><Sparkles size={15} /> À vous de jouer</span>
            <h2 className="font-display text-4xl cc-navy mt-2">CRÉEZ VOTRE COCARDE BUDGÉTAIRE</h2>
            <p className="text-slate-500 mt-2">Répartissez vos postes de dépenses, visualisez-les sur la cocarde, partagez.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div className="card-cocarde p-6">
              <div className="flex flex-wrap gap-2 mb-3">
                {Object.entries(TEMPLATES).map(([k, t]) => (
                  <button key={k} data-testid={`tpl-${k}`} onClick={() => applyTemplate(k)}
                    className="rounded-full bg-slate-100 hover:bg-slate-200 cc-navy text-xs font-bold px-3 py-1.5 transition-colors">{t.label}</button>
                ))}
              </div>
              <input data-testid="budget-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre (ex: Mon budget mensuel)"
                className="w-full border border-slate-300 rounded-xl px-4 py-3 mb-3 outline-none focus:border-cc-red font-bold cc-navy" />
              <div className="space-y-2">
                {rows.map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-cc-navy text-white text-[11px] font-black grid place-items-center shrink-0">{i + 1}</span>
                    <input data-testid={`row-label-${i}`} value={r.label} onChange={(e) => setRow(i, "label", e.target.value)} placeholder="Catégorie"
                      className="flex-1 border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-cc-red" />
                    <div className="relative w-20">
                      <input data-testid={`row-pct-${i}`} type="number" value={r.percent} onChange={(e) => setRow(i, "percent", e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-2 py-2 pr-5 outline-none focus:border-cc-red" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                    </div>
                    <button data-testid={`row-del-${i}`} onClick={() => delRow(i)} className="text-slate-400 hover:text-cc-red p-1"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
              <button data-testid="budget-add-row" onClick={addRow} className="mt-3 text-cc-red font-bold text-sm inline-flex items-center gap-1"><Plus size={15} /> Ajouter une part</button>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                <span className="text-sm text-slate-500">Total (auto-normalisé)</span>
                <span className={`font-display text-xl ${customTotal === 100 ? "text-green-600" : "text-cc-red"}`}>{customTotal}%</span>
              </div>
              <input data-testid="budget-author" value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Votre prénom ou entreprise (facultatif)"
                className="w-full border border-slate-300 rounded-xl px-4 py-2.5 mt-3 outline-none focus:border-cc-red" />
              <label className="mt-3 flex items-center gap-3 border border-dashed border-slate-300 rounded-xl px-4 py-2.5 cursor-pointer hover:border-cc-red">
                {logo ? <img src={resolveImg(logo)} alt="logo" className="w-9 h-9 rounded-full object-cover border border-slate-200" /> : <Upload size={18} className="text-slate-400" />}
                <span className="text-sm text-slate-500 font-semibold">{logo ? "Logo ajouté — changer" : "Ajouter le logo de votre entreprise (rond)"}</span>
                <input data-testid="budget-logo" type="file" accept="image/*" onChange={uploadLogo} className="hidden" />
              </label>
              <div className="flex gap-2 mt-3">
                <button data-testid="budget-submit" onClick={submit} disabled={busy} className="btn-cocarde flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  <Share2 size={16} /> {busy ? "Envoi…" : "Partager"}
                </button>
                <button data-testid="budget-export" onClick={exportPng} className="btn-navy inline-flex items-center justify-center gap-2 px-4">
                  <Download size={16} /> Aperçu
                </button>
              </div>
            </div>
            <div className="grid place-items-center">
              <div className="max-w-[320px] w-full relative" data-testid="builder-chart">
                <CocardeChart id="builder-svg" items={rows.filter((r) => Number(r.percent) > 0)} selected={builderSel} onSelect={(i) => setBuilderSel(builderSel === i ? -1 : i)} />
                {logo && <img src={resolveImg(logo)} alt="logo" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[24%] aspect-square rounded-full object-cover border-2 border-white shadow-lg" />}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-center gap-3 mb-6"><PieChart className="text-cc-red" /><h2 className="font-display text-3xl cc-navy">LES COCARDES DU PEUPLE</h2></div>
        <p className="text-slate-500 -mt-4 mb-6 text-sm">Votez pour votre cocarde budgétaire préférée — la mieux garnie gagne !</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {community.map((b, idx) => {
            const voted = typeof window !== "undefined" && localStorage.getItem(`cc_bvote_${b.id}`);
            return (
              <div key={b.id} data-testid={`community-${b.id}`} className="card-cocarde p-5">
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 shrink-0 relative">
                    <CocardeChart items={b.items} size={110} showNumbers={false} />
                    {b.logo_url && <img src={resolveImg(b.logo_url)} alt="" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[26%] aspect-square rounded-full object-cover border border-white" />}
                    {idx === 0 && b.votes > 0 && <span className="absolute -top-2 -right-2 bg-cc-gold text-cc-navy text-[10px] font-black rounded-full px-2 py-0.5">🏆 #1</span>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-lg cc-navy truncate">{b.title}</p>
                    <p className="text-slate-400 text-xs mb-1">par {b.author_name} · {b.theme}</p>
                    <p className="text-slate-500 text-xs truncate">{b.items.map((i) => i.label).join(", ")}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                  <span className="font-display text-lg text-cc-red">{b.votes || 0} <span className="text-xs text-slate-400 font-sans">votes</span></span>
                  <button data-testid={`community-vote-${b.id}`} onClick={() => voteBudget(b.id)} disabled={!!voted}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-bold transition-all ${voted ? "bg-slate-100 text-slate-400" : "bg-cc-red text-white hover:scale-105"}`}>
                    <ThumbsUp size={14} /> {voted ? "Voté" : "Voter"}
                  </button>
                </div>
              </div>
            );
          })}
          {community.length === 0 && <p className="text-slate-400">Aucune cocarde partagée pour l'instant.</p>}
        </div>
      </section>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-md bg-white">
          <DialogTitle className="font-display text-2xl cc-navy">Aperçu de votre cocarde</DialogTitle>
          <DialogDescription className="text-slate-500 text-sm -mt-1">Vérifiez le rendu (cocarde + légende + logo) avant de télécharger.</DialogDescription>
          {preview && <img data-testid="export-preview-img" src={preview} alt="aperçu cocarde" className="w-full rounded-xl border border-slate-200 mt-2" />}
          <a data-testid="export-download" href={preview || "#"} download="cocarde-pizzagraphie.png" onClick={() => toast.success("Image téléchargée — prête à poster !")}
            className="btn-cocarde w-full inline-flex items-center justify-center gap-2 mt-3">
            <Download size={16} /> Télécharger l'image
          </a>
        </DialogContent>
      </Dialog>
    </div>
  );
}
